import { EnvelopeFlowState, TasksStatus } from "@app/const/consts";
import {
  BooleanOptions,
  EnvelopeRecipientType,
  FiltersName,
  mockTransFunc,
} from "@const/translations";
import {
  FilterCondition,
  FilterRules,
  ILibFilters,
  ILibOption,
} from "@infologistics/frontend-libraries";
import { flowStateSettings } from "../../consts";
import {
  FILTER_DATE_TIME_OPTIONS,
  FILTER_DATE_RULES,
} from "@app/const/filters";
import { IEnvelopeField, ILabel, ILabelGroup } from "@store/modules/boxes/types";
import { EnvelopeDirection } from "@const/consts";
import { getEnvelopeFieldFilters } from "../dynamicFields";

enum FILTERS_NAMES {
  senderFullName = "sender_fullName",
  senderEmail = "sender_email",
  recipientFullName = "recipient_fullName",
  recipientEmail = "recipient_email",
  subject = "subject",
  flowState = "flowState",
  taskTypes = "taskTypes",
  isActionRequired = "isActionRequired",
  labelKey = "labelKey",
  flowStarted = "flowStarted",
  flowCompleted = "flowCompleted",
  flowLastActionDate = "flowLastActionDate",
}

const ENVELOPES_FILTERS_ORDER = [
  FILTERS_NAMES.senderFullName,
  FILTERS_NAMES.senderEmail,
  FILTERS_NAMES.recipientFullName,
  FILTERS_NAMES.recipientEmail,
  FILTERS_NAMES.subject,
  FILTERS_NAMES.flowState,
  FILTERS_NAMES.taskTypes,
  FILTERS_NAMES.isActionRequired,
  FILTERS_NAMES.labelKey,
  FILTERS_NAMES.flowStarted,
  FILTERS_NAMES.flowCompleted,
  FILTERS_NAMES.flowLastActionDate,
];

export const DEFAULT_VISIBLE_ENVELOPES_FILTERS = [
  FILTERS_NAMES.senderEmail,
  FILTERS_NAMES.recipientEmail,
  FILTERS_NAMES.flowState,
  FILTERS_NAMES.flowCompleted,
];

export const ENVELOPES_FILTERS_DATES = [
  "flowStarted",
  "flowCompleted",
  "flowLastActionDate",
];

const FLOW_STATE_VALUES: ILibOption[] = Object.keys(EnvelopeFlowState).map(
  (val) => ({
    label: mockTransFunc(flowStateSettings[val].title),
    value: val,
  })
);

const TASK_TYPE_VALUES: ILibOption[] = Object.values(TasksStatus).map(
  (value) => ({
    label: EnvelopeRecipientType[value],
    value,
  })
);

const envelopesFiltersData: ILibFilters = {
  [FILTERS_NAMES.senderFullName]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.senderFullName,
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.CONTAINS]: FilterRules.TEXT,
    },
    translation: FiltersName.senderName,
  },
  [FILTERS_NAMES.senderEmail]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.senderEmail,
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.CONTAINS]: FilterRules.TEXT,
    },
    translation: FiltersName.senderEmail,
  },
  [FILTERS_NAMES.recipientFullName]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.recipientFullName,
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.CONTAINS]: FilterRules.TEXT,
    },
    translation: FiltersName.recipientName,
  },
  [FILTERS_NAMES.recipientEmail]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.recipientEmail,
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.CONTAINS]: FilterRules.TEXT,
    },
    translation: FiltersName.recipientEmail,
  },
  [FILTERS_NAMES.subject]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.subject,
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.CONTAINS]: FilterRules.TEXT,
    },
    translation: FiltersName.subject,
  },
  [FILTERS_NAMES.flowState]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.flowState,
    options: FLOW_STATE_VALUES,
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.EQ]: FilterRules.SELECT,
    },
    translation: FiltersName.flowState,
  },
  [FILTERS_NAMES.taskTypes]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.taskTypes,
    options: TASK_TYPE_VALUES,
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.EQ]: FilterRules.MULTISELECT,
    },
    translation: FiltersName.taskType,
  },
  [FILTERS_NAMES.isActionRequired]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.isActionRequired,
    options: [
      {
        label: BooleanOptions.true,
        value: "true",
      },
      {
        label: BooleanOptions.false,
        value: "false",
      },
    ],
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.EQ]: FilterRules.SELECT,
    },
    translation: FiltersName.isActionRequired,
  },
  [FILTERS_NAMES.labelKey]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.labelKey,
    options: [],
    rules: {
      [FilterCondition.ALL]: FilterRules.ALL,
      [FilterCondition.EQ]: FilterRules.MULTISELECT,
    },
    translation: FiltersName.label,
  },
  [FILTERS_NAMES.flowStarted]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.flowStarted,
    options: FILTER_DATE_TIME_OPTIONS,
    rules: FILTER_DATE_RULES,
    translation: FiltersName.flowStarted,
  },
  [FILTERS_NAMES.flowCompleted]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.flowCompleted,
    options: FILTER_DATE_TIME_OPTIONS,
    rules: FILTER_DATE_RULES,
    translation: FiltersName.flowCompleted,
  },
  [FILTERS_NAMES.flowLastActionDate]: {
    condition: FilterCondition.ALL,
    name: FILTERS_NAMES.flowLastActionDate,
    options: FILTER_DATE_TIME_OPTIONS,
    rules: FILTER_DATE_RULES,
    translation: FiltersName.flowLastActionDate,
  },
};

export const envelopesFilters = {
  data: envelopesFiltersData,
  order: ENVELOPES_FILTERS_ORDER,
};

export const sortEnvelopeLabels = (
  labels: ILabel[],
  groups: ILabelGroup[] = []
): ILabel[] => {
  const groupOrder = new Map(groups.map(({ key }, index) => [key, index + 1]));
  let nextGroupOrder = groupOrder.size + 1;

  labels.forEach(({ group }) => {
    if (group && !groupOrder.has(group.key)) {
      groupOrder.set(group.key, nextGroupOrder++);
    }
  });

  return labels
    .map((label, index) => ({
      index,
      label,
      groupOrder: label.group ? groupOrder.get(label.group.key) ?? nextGroupOrder : 0,
    }))
    .sort((first, second) => (
      first.groupOrder - second.groupOrder || first.index - second.index
    ))
    .map(({ label }) => label);
};

export const getEnvelopesFilters = (
  fields: IEnvelopeField[],
  direction?: EnvelopeDirection | null,
  boxOguid?: string,
  labels: ILabel[] = []
) => {
  const systemFilters = Object.entries(envelopesFiltersData).reduce<ILibFilters>(
    (acc, [filterName, filter]) => ({
      ...acc,
      [filterName]: {
        ...filter,
        isAdditional: true,
      },
    }),
    {}
  );
  const fieldFilters = getEnvelopeFieldFilters(fields, direction, boxOguid);
  systemFilters[FILTERS_NAMES.labelKey] = {
    ...systemFilters[FILTERS_NAMES.labelKey],
    options: sortEnvelopeLabels(labels).map((label) => ({
      label: label.group ? `${label.group.name} / ${label.name}` : label.name,
      value: label.fullKey,
    })),
  };
  const fieldFiltersOrder = Object.keys(fieldFilters);

  return {
    data: {
      ...systemFilters,
      ...fieldFilters,
    },
    order: [
      ...ENVELOPES_FILTERS_ORDER,
      ...fieldFiltersOrder,
    ],
  };
};
